import { UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";

import { MoneyText, useYen } from "@/components/MoneyText";
import { NumPad } from "@/components/NumPad";
import type { CartCustomer, TenderPreview } from "@/lib/types";

import type { TenderPart } from "./useTender";

/**
 * ストアクレジット: needs the customer (else "choose the customer" opens the customer dialog);
 * shows the balance; part of the amount may be paid with it (the rest by cash / card = 分割).
 */
export function StoreCreditForm({
  part,
  customer,
  preview,
  onAmount,
  onChooseCustomer,
}: {
  part: TenderPart;
  customer: CartCustomer | null;
  preview: TenderPreview | undefined;
  onAmount: (digits: string) => void;
  onChooseCustomer: () => void;
}) {
  const { t } = useTranslation();
  const yen = useYen();
  if (!customer) {
    return (
      <div className="flex flex-col items-start gap-3 rounded-md border border-status-pending bg-status-pending/15 p-4">
        <p className="text-lg">{t("tender.credit_needs_customer")}</p>
        <button
          type="button"
          onClick={onChooseCustomer}
          className="flex min-h-pos-touch items-center gap-2 rounded-md border bg-background px-6 text-lg"
        >
          <UserRound aria-hidden />
          {t("tender.credit_choose_customer")}
        </button>
      </div>
    );
  }
  const usable = preview?.storeCreditUsable ?? null;
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_16rem]">
      <NumPad
        label={t("tender.credit_use")}
        value={part.amount}
        onChange={onAmount}
        money
        maxLength={9}
      />
      <div className="flex flex-col gap-3">
        <p className="text-lg">
          {customer.name ?? customer.email}
          {typeof customer.storeCreditBalance === "number" ? (
            <span className="block text-base text-muted-foreground tabular-nums">
              {t("tender.credit_balance", { amount: yen(customer.storeCreditBalance) })}
            </span>
          ) : null}
        </p>
        {usable !== null && usable > 0 ? (
          <button
            type="button"
            onClick={() => onAmount(String(usable))}
            className="min-h-pos-touch rounded-md border text-lg"
          >
            {t("tender.credit_use_all")} (<MoneyText amount={usable} />)
          </button>
        ) : null}
      </div>
    </div>
  );
}
